import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BadgePlus, SquarePen } from 'lucide-react';
import { GiftDTO, useCreateGift, useUpdateGift } from '@/hooks/useGifts';

import LoadingCircle from '@/components/LoadingCircle';
import { SupplierDTO } from '@/hooks/useSuppliers';
import { GiftFormValues, giftFormSchema } from '@/schemas/gift';
import { Textarea } from '@/components/ui/textarea';
import TextInActive from '@/components/TextInActive';
import { supplierStatus } from '@/api/suppliers';
interface CreateUpdateProps {
  gift?: GiftDTO;
  suppliers: SupplierDTO[];
}

const CreateUpdate = ({ gift, suppliers }: CreateUpdateProps) => {
  const [open, setOpen] = useState(false);
  const isEdit = !!gift;

  const { mutateAsync: createGift, isPending } = useCreateGift();
  const { mutateAsync: updateGift, isPending: isUpdating } = useUpdateGift();

  const form = useForm<GiftFormValues>({
    resolver: zodResolver(giftFormSchema),
    defaultValues: {
      name: gift?.name || '',
      description: gift?.description || '',
      remainingQuantity: gift?.remainingQuantity,
      unitPrice: gift?.unitPrice,
      supplierId: gift?.supplier.id.toString() || '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (gift) {
      form.reset({
        name: gift.name,
        description: gift.description,
        remainingQuantity: gift.remainingQuantity,
        unitPrice: gift.unitPrice,
        supplierId: gift.supplier.id.toString(),
      });
    }
  }, [gift, form]);

  async function onSubmit(values: GiftFormValues) {
    if (isEdit) {
      await updateGift({
        id: gift.id.toString(),
        data: {
          ...values,
          remainingQuantity: values.remainingQuantity,
          unitPrice: values.unitPrice,
          supplierId: parseInt(values.supplierId),
        },
      });
    } else {
      await createGift({
        ...values,
        remainingQuantity: values.remainingQuantity,
        unitPrice: values.unitPrice,
        supplierId: parseInt(values.supplierId),
      });
    }
    setOpen(false);
    form.reset();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        form.reset();
        setOpen(value);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant={isEdit ? 'outline' : 'default'}
          className={isEdit ? 'luxury-button-outline' : 'luxury-button-primary'}
        >
          {isEdit ? (
            <>
              <SquarePen className="w-4 h-4" />
              Sửa
            </>
          ) : (
            <>
              <BadgePlus className="w-4 h-4" />
              Thêm mới
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-[800px] max-h-[95vh] overflow-y-auto luxury-theme">
        <DialogDescription className="sr-only">
          {isEdit ? 'Sửa quà tặng' : 'Thêm quà tặng'}
        </DialogDescription>
        <DialogHeader className="luxury-header">
          <DialogTitle className="luxury-title">
            {isEdit ? 'Cập nhật quà tặng' : 'Tạo quà tặng mới'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="luxury-card">
              <h3 className="luxury-subtitle mb-4">Thông tin quà tặng</h3>
              <div className="flex flex-col gap-6 items-start">
                <div className="flex gap-6 w-full items-start">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Tên quà tặng
                        </Label>
                        <FormControl>
                          <Input {...field} className="luxury-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="supplierId"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Nhà cung cấp
                        </Label>
                        <Select
                          value={field.value?.toString()}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full luxury-input">
                              <SelectValue placeholder="Chọn nhà cung cấp" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="border-luxury-200">
                            {suppliers
                              ?.filter(
                                (supplier) =>
                                  supplier.status === supplierStatus.ACTIVE ||
                                  (isEdit &&
                                    supplier.id.toString() ===
                                      gift?.supplier.id.toString())
                              )
                              .map((supplier: SupplierDTO) => (
                                <SelectItem
                                  key={supplier.id}
                                  value={supplier.id.toString()}
                                  disabled={
                                    supplier.status === supplierStatus.INACTIVE
                                  }
                                >
                                  {supplier.name}
                                  <TextInActive status={supplier.status} />
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex gap-6 w-full items-start">
                  <FormField
                    control={form.control}
                    name="remainingQuantity"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Số lượng còn lại
                        </Label>
                        <FormControl>
                          <Input
                            value={field.value}
                            type="number"
                            className="luxury-input"
                            onChange={(e) => {
                              field.onChange(parseInt(e.target.value));
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="unitPrice"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Đơn giá (VNĐ)
                        </Label>
                        <FormControl>
                          <Input
                            value={field.value}
                            type="number"
                            className="luxury-input"
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-full">
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <Label required className="luxury-label">
                          Mô tả
                        </Label>
                        <FormControl>
                          <Textarea
                            {...field}
                            className="luxury-input"
                            rows={4}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full mt-6 luxury-button-primary"
              disabled={isPending || isUpdating}
            >
              {isPending || isUpdating ? (
                <LoadingCircle />
              ) : (
                <>{isEdit ? 'Cập nhật quà tặng' : 'Tạo quà tặng mới'}</>
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUpdate;
